/**
 * @fileoverview Job KYC & AML checks tab (MOR-478).
 *
 * URL: /dashboard/jobs/:jobId/searches/kyc-aml
 *
 * AML screening (UBO + PEPs) and InfoTrackID remote identity verification,
 * both via the firm's InfoTrack API key. Completed reports land in the job's
 * document library like every other search.
 */

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Info, KeyRound } from "lucide-react";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { SearchesHistoryTable } from "./-ui.searches-history-table";
import { SearchesKycTrigger } from "./-ui.searches-kyc-trigger";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/searches/kyc-aml",
)({
  component: JobKycAmlChecksPage,
});

function PageHeader() {
  return (
    <div>
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          KYC &amp; AML checks
        </h1>
        <Badge color="violet" variant="soft" size="md">
          Beta
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Powered by InfoTrack. Run AML screening (UBO / PEPs) and remote identity
        verification. All reports will be added to the Job's Data.
      </p>
    </div>
  );
}

function JobKycAmlChecksPage() {
  const { jobId } = Route.useParams();

  const { data: job } = useQuery({
    ...convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  });

  // Same per-firm key gate as Registry searches — one InfoTrack credential
  // covers every InfoTrack-backed product.
  const { data: keyStatus } = useQuery({
    ...convexQueryOrIdle(
      api.settings.infotrack.queries.getInfotrackKeyStatus,
      job?.orgId ? { orgId: job.orgId } : undefined,
    ),
    enabled: !!job?.orgId,
  });

  const { data: orderRows } = useQuery({
    ...convexQuery(api.jobs.searches.queries.listOrdersForJob, {
      jobId: jobId as Id<"jobs">,
    }),
  });

  // This page shows only KYC & AML orders; registry searches stay on their tab.
  const kycOrderRows = orderRows?.filter(
    (r) => r.registry === "aml" || r.registry === "infotrackid",
  );

  const [selectedDocId, setSelectedDocId] = useState<Id<"documents"> | null>(
    null,
  );

  const isViewer = job?.userRole === "viewer";

  if (keyStatus && !keyStatus.configured) {
    return (
      <div className="flex flex-1 flex-col bg-muted p-6">
        <PageHeader />
        <div className="flex flex-1 items-center justify-center">
          <div className={`${GROUP_CARD_CLASS} w-full max-w-md`}>
            <div className="flex flex-col items-center gap-4 px-8 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <KeyRound className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-lg font-semibold tracking-tight">
                  Configure your InfoTrack API key
                </h2>
                <p className="text-sm text-muted-foreground">
                  Your firm hasn't connected InfoTrack yet. Add your API key in
                  Settings → Integrations before you can run checks.
                </p>
              </div>
              <Button asChild>
                <Link to="/dashboard/settings/integrations">
                  Go to Settings → Integrations
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <PageHeader />

      {/* Beta + billing notice — same terms as Registry searches */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 dark:border-amber-800 dark:bg-amber-950/40">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              InfoTrack searches are in beta
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Running a check will incur your existing InfoTrack rates per
              search. You'll be charged and billed directly by InfoTrack in your
              normal manner — Morgan does not add any charges on top.
            </p>
          </div>
        </div>
      </div>

      {/* New check */}
      <div className={GROUP_CARD_CLASS}>
        <div className="space-y-4 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              Run a check
            </h2>
            <p className="text-sm text-muted-foreground">
              AML screening completes in minutes. Identity verification emails
              the subject a link and completes when they finish — leave it
              running.
            </p>
          </div>
          <SearchesKycTrigger jobId={jobId as Id<"jobs">} />
        </div>
      </div>

      {/* Check history */}
      <h2 className="pt-4 text-lg font-semibold tracking-tight">
        Check history
      </h2>
      <p className="text-sm text-muted-foreground">
        Every KYC &amp; AML check on this job — in progress, completed, and
        failed.
      </p>
      <div className={GROUP_CARD_CLASS}>
        <div className="px-5 py-4">
          {kycOrderRows ? (
            <SearchesHistoryTable
              jobId={jobId as Id<"jobs">}
              rows={kycOrderRows}
              onOpenDocument={(id) => setSelectedDocId(id)}
            />
          ) : (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
        </div>
      </div>

      <DocumentDetail
        documentId={selectedDocId}
        open={selectedDocId !== null}
        isViewer={isViewer}
        onClose={() => setSelectedDocId(null)}
        defaultTab="original"
      />
    </div>
  );
}
